import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle2, Mail, Sparkles, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Please enter a valid email address.' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email, source: 'landing' });
      if (data.already_joined) {
        setJoined(true);
        toast({
          title: "You're already on the list.",
          description: "We'll be in touch soon.",
        });
      } else {
        setJoined(true);
        toast({
          title: "You're on the list!",
          description: 'Confirmation email is on its way \u2014 founding member perks locked in.',
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast({ title: 'Signup failed', description: typeof msg === 'string' ? msg : 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="relative py-24 md:py-32 bg-black overflow-hidden">
      <div className="absolute inset-0 hero-neon-bg opacity-70 pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#39D353]/10 border border-[#39D353]/25 text-[11px] tracking-[0.15em] uppercase text-[#39D353] font-semibold fade-up">
          <Sparkles size={12} />
          Early access · limited seats
        </div>

        <h2 className="mt-6 text-[clamp(2rem,5vw,3.75rem)] leading-[1.02] font-bold tracking-tight fade-up-2">
          <span className="text-white">Reserve your spot in</span>{' '}
          <span className="neon-text italic">the future</span>{' '}
          <span className="text-white">of luxury.</span>
        </h2>
        <p className="mt-5 text-neutral-400 text-base md:text-lg leading-relaxed max-w-xl mx-auto fade-up-2">
          Drop your email below. We&apos;ll send an invitation to the private beta before we launch
          publicly.
        </p>

        {!joined ? (
          <form onSubmit={handleSubmit} className="mt-10 max-w-lg mx-auto fade-up-3">
            <div className="flex flex-col sm:flex-row gap-2 bg-white/5 border border-white/10 rounded-full p-1.5 backdrop-blur-sm">
              <div className="flex items-center flex-1 pl-4">
                <Mail size={16} className="text-neutral-500 shrink-0" />
                <input
                  type="email"
                  placeholder="you@wallet.eth"
                  value={email}
                  disabled={loading}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent px-3 h-11 outline-none text-sm text-white placeholder:text-neutral-500 disabled:opacity-50"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-neon inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full text-sm font-semibold whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Joining…
                  </>
                ) : (
                  <>
                    Join the Waitlist
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-xs text-neutral-500">
              <div className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-[#39D353]" /> No spam</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-[#39D353]" /> Unsubscribe anytime</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-[#39D353]" /> Priority beta invite</div>
            </div>
          </form>
        ) : (
          <div className="mt-10 max-w-lg mx-auto fade-up-3">
            <div className="relative rounded-2xl border border-[#39D353]/30 bg-[#39D353]/5 p-6 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[#39D353] text-black flex items-center justify-center shrink-0">
                  <CheckCircle2 size={20} />
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold text-white">You&apos;re in the club.</div>
                  <div className="mt-1 text-sm text-neutral-300">
                    Confirmation email is on the way. Watch your inbox for private updates and your beta invitation.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WaitlistForm;
